import { API_ENDPOINTS, resolveApiBaseUrl } from '@tsgecart/config';
import type { ApiResponse, ApiErrorCode } from '@tsgecart/shared';
import { webEnv } from '../env.js';
import type { AuthMeResponse } from './types.js';

const apiBaseUrl = resolveApiBaseUrl({
  environment: webEnv.VITE_APP_ENV,
  configuredUrl: webEnv.VITE_API_BASE_URL,
});

/** Carries the API's error code/HTTP status so callers can branch (e.g. AUTHORIZATION_ERROR → blocked account, 401 → expired session) without string-matching a message. */
export class AuthMeError extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AuthMeError';
  }
}

/**
 * Calls the Worker's GET /api/v1/auth/me with the caller's current Clerk
 * session token. No credentials/cookies are sent — this API authenticates
 * exclusively via the Bearer token, so there is nothing for a cookie to add
 * and sending one would be needless cross-origin exposure.
 */
export async function fetchAuthMe(token: string): Promise<AuthMeResponse> {
  const response = await fetch(`${apiBaseUrl}${API_ENDPOINTS.authMe}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = (await response.json()) as ApiResponse<AuthMeResponse>;
  if (!body.success) {
    throw new AuthMeError(body.error.code, body.error.message, response.status);
  }
  return body.data;
}
